/**
 * Filtry zdarzeń Sentry rozpoznające błędy, które NIE pochodzą z bramki.
 *
 * Wszystkie cztery predykaty są wołane z `beforeSend` (patrz `sentryInit.js`) i wszystkie cztery
 * istnieją dlatego, że `ignoreErrors` dopasowuje się do KOMUNIKATU, a tu komunikat nie niesie informacji:
 * odrzucenia z SDK Click to Pay mają komunikat generyczny (payload siedzi w `hint`), kod wstrzykiwany
 * w dokument przez rozszerzenia i in-app browsery ma komunikaty dowolne, zmienne z każdą ich wersją,
 * a część z nich jest tak generyczna, że wzorzec na treść wyciszyłby kiedyś nasz własny błąd.
 * Rozpoznajemy więc STRUKTURĘ zdarzenia.
 *
 * Predykaty są czyste i nie dotykają SDK - stąd osobny plik i testy wprost na kształtach zdarzeń
 * zdjętych z produkcji (`tests/unit/utils/sentryFilters.spec.js`).
 *
 * ⚠️ Trzy z nich wymagają STOSU (`frames.length > 0`) - i to jest warunek celowy, żeby nie wyciszać
 * naszych własnych `reportMessage`, które stosu nie mają. Czwarty (`isValuelessRejection`) obsługuje
 * dokładnie tę lukę: odrzucenie, które nie niesie ani wartości, ani stosu.
 */

/**
 * Metoda rozpoznająca odrzucenia pochodzące z SDK Click to Pay (`srcsdk.mastercard.js`, widget Visa SRC).
 *
 * Dlaczego filtr, a nie `.catch` w kodzie: audyt wszystkich wywołań akcji C2P (komponenty, mixiny,
 * klasy opakowujące, store) wykazał, że KAŻDE ma obsługę odrzucenia. Ten reject powstaje wewnątrz
 * samego SDK dostawcy - żaden nasz `.catch` nie ma go jak przechwycić. Potwierdzenie z produkcji:
 * poprawka w `store/modules/c2p.js` zlikwidowała wyciek z łańcucha inicjalizacji (zgłoszenie ze
 * `SRCError` zamilkło co do minuty), a ten strumień szedł dalej niezmiennym tempem jeszcze godzinę
 * po wdrożeniu - czyli już poza sesjami na starym bundlu.
 *
 * Dlaczego nie `ignoreErrors`: to odrzucenie obiektu, nie `Error`-a, więc komunikatem zdarzenia jest
 * generyczne „Non-Error promise rejection captured with keys: error", a właściwy payload siedzi
 * w `hint.originalException`, którego `ignoreErrors` w ogóle nie ogląda. Wzorzec na sam komunikat
 * wyciszyłby każde odrzucenie obiektu z kluczem `error`, z dowolnego miejsca w aplikacji.
 *
 * Rozpoznajemy po słowniku SRC (`REQUEST_TIMEOUT`, `SRCError`), a nie po treści komunikatu - dzięki
 * temu filtr nie łapie niczego spoza Click to Pay.
 *
 * Awaria SDK nie jest błędem aplikacji: Click to Pay sam się wtedy wyłącza
 * (`SET_C2P_IS_PROCESS_AVAILABLE` = false), a przyczyna trafia do `logC2PTransaction`.
 *
 * @param {unknown} originalException surowa wartość odrzucenia (`hint.originalException`)
 * @returns {boolean}
 */
export function isClick2PaySdkRejection(originalException) {
  if (!originalException || typeof originalException !== 'object') {
    return false
  }
  // SDK Mastercard pakuje błąd w `{ error: {...} }`, Visa SRC zwraca go płasko
  const payload =
    originalException.error && typeof originalException.error === 'object'
      ? originalException.error
      : originalException

  return payload.reason === 'REQUEST_TIMEOUT' || payload.name === 'SRCError'
}

/**
 * Komunikaty, których NIE wolno wyciszyć filtrem `isInjectedDocumentScriptError`, choć spełniają jego
 * warunki strukturalne:
 * awaria dynamicznego `import()` chunka też jest atrybuowana do dokumentu HTML (nie do pliku `.js`),
 * a jest realnym problemem aplikacji na WebKicie - patrz `doc/robocze/webkit-import-chunkow.md`.
 */
const ALWAYS_REPORTED_MESSAGES = [/module script failed/i, /dynamically imported module/i, /loadLanguage/]

const isScriptFileFrame = (filename) =>
  typeof filename === 'string' && /\.js(\?|$)/.test(filename.split('#')[0])

/**
 * Chunk z SDK Sentry. `Sentry.wrap()` opakowuje KAŻDY listener zdarzeń w dokumencie, więc ta klatka
 * siedzi na szczycie każdego stosu złapanego przez `addEventListener` - także stosu złożonego poza
 * tym wyłącznie z kodu firmy trzeciej. Traktowanie jej jako klatki „naszej" przewracało oba filtry
 * strukturalne: `isBrowserExtensionError` przepuszczał most in-app browsera Facebooka (`iabjs://`,
 * ~8 zdarzeń na dobę, pomiar 17.08.2026: `sentry.<hash>.js` + trzy klatki `iabjs://` i nic więcej),
 * a `isInjectedDocumentScriptError` - wstrzyknięty kod, którego jedynym śladem w stosie jest właśnie
 * ten wrapper (most natywny `ReactNativeHistoryShim`, warstwa `scanForForms`; pomiar 18.08.2026).
 *
 * Zdarzenie z naszego kodu ma w stosie także jakiś inny chunk (`Paywall.<hash>.js`, `vendor.<hash>.js`,
 * dokument bramki), więc uznanie samego wrappera za neutralny niczego naszego nie wycisza.
 */
const isSentrySdkFrame = (filename) => /(^|\/)sentry(-legacy)?\.[A-Za-z0-9_-]+\.js$/.test(filename)

/**
 * Klatka wskazująca plik `.js` POBRANY DO STRONY - nasz chunk albo SDK dostawcy. Wrapper SDK Sentry
 * się nie liczy (patrz `isSentrySdkFrame`).
 */
const isAppScriptFrame = (filename) => isScriptFileFrame(filename) && !isSentrySdkFrame(filename)

/**
 * Spłaszcza klatki ze wszystkich wyjątków zdarzenia do jednej tablicy.
 *
 * @param {object} event zdarzenie Sentry (kształt SDK 6.x)
 * @returns {Array<object>} klatki stosu; pusta tablica, gdy zdarzenie nie ma wyjątku ani stosu
 */
const getEventFrames = (event) => {
  const values = event?.exception?.values
  if (!Array.isArray(values) || values.length === 0) {
    return []
  }

  return values.reduce((all, value) => all.concat(value?.stacktrace?.frames || []), [])
}

/**
 * Metoda rozpoznająca błędy kodu WSTRZYKNIĘTEGO W DOKUMENT - przez rozszerzenia, przeglądarki
 * wbudowane w aplikacje (in-app browsery banków i komunikatorów), warstwy autofillu, tłumaczenia
 * i mosty natywne. Taki kod wykonuje się w kontekście strony, więc jego wyjątki łapie `window.onerror`
 * / `unhandledrejection` podpięte przez Sentry i wyglądają jak nasze.
 *
 * Rozpoznanie: zdarzenie MA stos, a w całym stosie NIE MA ani jednej klatki wskazującej plik `.js`
 * pobrany do strony. Nasz kod siedzi wyłącznie w `/web/js/*.js`, więc taki stos nie może być nasz.
 *
 * Filtr powstał jako zawężony do aplikacji Google na iOS (Chrome iOS `CriOS/`, aplikacja Google
 * `GSA/` - warstwa autofillu `__gCrWeb` w WKWebView). Pomiar 12.08.2026, 5 grup / 147 zdarzeń
 * (~63 na dobę u ~60 użytkowników): UA Google w **147/147**, iOS 100%, ZERO klatek z `/web/js/*`,
 * klatki wskazywały `undefined` albo dokument HTML w liniach 189-196 i 415-454 - przy dokumencie,
 * który ma **87 linii**.
 *
 * Warunek UA zdjęty 18.08.2026: dokładnie ten sam kształt stosu miało **178 zdarzeń na dobę w 44
 * grupach**, z każdej przeglądarki - `null (reading 'addEventListener')` 35 i `null (reading 'click')`
 * 17 (Chrome Mobile / Android, cały stos to `<anonymous>`), `wallet must has at least one account` 16
 * (rozszerzenie kryptowalutowe), `ReactNativeHistoryShim` 10, `SymBrowser_*` 10 (in-app browser
 * Nortona), `Unexpected token 'else'` 10 i `illegal character U+009E` (skrypty wstrzyknięte w treść
 * dokumentu), mosty natywne (`Java bridge method invocation error`, `WKWebView API client did not
 * respond`, `Method not found`, `Target window is closed`).
 *
 * Dlaczego nie `ignoreErrors`: komunikaty są dowolne i zmieniają się z każdą wersją rozszerzenia,
 * a część z nich jest tak generyczna (`null (reading 'click')`, `Method not found`), że wzorzec na
 * treść wyciszyłby kiedyś realną regresję w naszym kodzie. Struktura stosu tego ryzyka nie ma:
 * nasz błąd ma w stosie plik `.js`.
 *
 * Warunki (wszystkie muszą być spełnione):
 * 1. zdarzenie MA stos - `reportMessage` (`captureMessage`) go nie ma i musi przechodzić dalej,
 *    inaczej wyciszylibyśmy własne raporty (np. „SecureBack: powrót z banku bez parametrów");
 * 2. ŻADNA klatka nie wskazuje pliku `.js` pobranego do strony - błędy zewnętrznych SDK, które
 *    raportujemy świadomie (np. `/gp/p/js/pay.js` z Google Pay), takie klatki mają i przechodzą.
 *    Wrapper `sentry.<hash>.js` się nie liczy, patrz `isSentrySdkFrame`;
 * 3. komunikat nie jest na liście `ALWAYS_REPORTED_MESSAGES`.
 *
 * Świadomy koszt: awaria naszych skryptów inline z `public/index.html` (Matomo, yourcx) oraz modułu
 * `data:text/javascript` z `@vitejs/plugin-legacy` jest tym filtrem wyciszona - w stosie nie mają
 * pliku `.js`. To kod firm trzecich i snippet wykrywający wsparcie modułów, nie logika bramki.
 *
 * @param {object} event zdarzenie Sentry (kształt SDK 6.x)
 * @returns {boolean}
 */
export function isInjectedDocumentScriptError(event) {
  const values = event?.exception?.values
  if (!Array.isArray(values) || values.length === 0) {
    return false
  }

  if (values.some((value) => ALWAYS_REPORTED_MESSAGES.some((pattern) => pattern.test(value?.value || '')))) {
    return false
  }

  const frames = getEventFrames(event)
  if (frames.length === 0) {
    return false
  }

  return frames.every((frame) => !isAppScriptFrame(frame?.filename))
}

/**
 * Adresy, spod których ładuje się kod rozszerzeń przeglądarki oraz warstw JS wstrzykiwanych przez
 * przeglądarki wbudowane w aplikacje (in-app browser, np. `iabjs://`). Nasz bundel nie trafia tam nigdy.
 */
const THIRD_PARTY_CODE_URL = /^(chrome|moz|safari-web|safari)-extension:|^webkit-masked-url:|^iabjs:/

/**
 * Klatki, które same z siebie niczego nie przesądzają - WebKit i V8 wstawiają je w KAŻDY stos,
 * także w stosy naszego kodu. Nie mogą więc ani kwalifikować, ani dyskwalifikować zdarzenia.
 */
const isNeutralFrame = (filename) =>
  !filename || filename === '<anonymous>' || filename === '[native code]' || isSentrySdkFrame(filename)

/**
 * Klatka wskazująca zasób pobrany po sieci - nasz chunk, dokument HTML albo SDK dostawcy.
 */
const isPageFrame = (filename) => /^https?:/.test(filename) || filename.startsWith('/')

/**
 * Safari nie podaje prawdziwego adresu kodu rozszerzenia - maskuje go jako `webkit-masked-url://hidden/`.
 * Sentry rozbija to na `filename: "//hidden/"` i przykleja schemat do nazwy funkcji
 * (`getBgImageValue@webkit-masked-url:`), więc rozpoznajemy oba miejsca.
 */
const isThirdPartyCodeFrame = (frame) => {
  const filename = typeof frame?.filename === 'string' ? frame.filename : ''
  const fn = typeof frame?.function === 'string' ? frame.function : ''

  return THIRD_PARTY_CODE_URL.test(filename) || filename === '//hidden/' || fn.includes('@webkit-masked-url:')
}

/**
 * Metoda rozpoznająca błędy pochodzące WYŁĄCZNIE z rozszerzeń przeglądarki i z warstw JS
 * wstrzykiwanych przez przeglądarki wbudowane w aplikacje.
 *
 * Pomiar z produkcji (13.08.2026, doba): ~130 zdarzeń dziennie w kilkunastu grupach, m.in.
 * `Cannot read properties of undefined (reading 'M_ID')` (61), `Failed to connect to MetaMask` (17),
 * `Cannot destructure property 'rule' from null` (17), `No Listener: tabs:outgoing.message.ready` (10),
 * `Talisman extension has not been configured yet` (9). Każde z nich ma stos złożony wyłącznie
 * z plików rozszerzenia - użytkownik bramki nie ma z tym nic wspólnego i nic z tym nie zrobi.
 *
 * Dlaczego nie `ignoreErrors`: komunikaty są dowolne i zmieniają się z każdą wersją rozszerzenia,
 * a jest ich tyle, ile rozszerzeń na świecie. Rozpoznajemy więc POCHODZENIE KODU, nie treść.
 *
 * Warunki (oba muszą być spełnione):
 * 1. co najmniej jedna klatka pochodzi z kodu firmy trzeciej - inaczej filtr nie ma podstaw;
 * 2. ŻADNA klatka nie wskazuje zasobu pobranego po sieci - jeśli w stosie jest nasz chunk, dokument
 *    bramki albo SDK dostawcy (`/gp/p/js/pay.js`), to rozszerzenie najwyżej dotknęło naszego kodu
 *    i zdarzenie musi przejść.
 *
 * Klatki `[native code]` i `<anonymous>` są pomijane w obu warunkach - występują też w stosach
 * naszego kodu, więc ani nie kwalifikują, ani nie dyskwalifikują zdarzenia.
 *
 * @param {object} event zdarzenie Sentry (kształt SDK 6.x)
 * @returns {boolean}
 */
export function isBrowserExtensionError(event) {
  const frames = getEventFrames(event)
  if (frames.length === 0) {
    return false
  }

  let hasThirdPartyFrame = false

  for (const frame of frames) {
    if (isThirdPartyCodeFrame(frame)) {
      hasThirdPartyFrame = true
      continue
    }

    const filename = typeof frame?.filename === 'string' ? frame.filename : ''
    if (isNeutralFrame(filename)) {
      continue
    }

    if (isPageFrame(filename)) {
      return false
    }
  }

  return hasThirdPartyFrame
}

/**
 * Mechanizm, którym SDK oznacza zdarzenia złapane przez `window.onunhandledrejection`.
 */
const UNHANDLED_REJECTION_MECHANISM = 'onunhandledrejection'

/**
 * Metoda rozpoznająca ODRZUCENIE BEZ WARTOŚCI I BEZ STOSU - `Promise` odrzucony wartością `null`
 * (albo `undefined`) przez kod, którego pochodzenia przeglądarka nie podaje.
 *
 * Takie zdarzenie nie niesie ŻADNEJ informacji nadającej się do diagnozy: komunikat generuje samo
 * SDK (`Non-Error promise rejection captured with value: null`), stosu nie ma - bo odrzucenie
 * wartością nie-`Error` nie ma czego nieść, a `attachStacktrace` jest wyłączone - i nie ma
 * breadcrumbów, bo rzut następuje przed pierwszym `fetch`/`console` w dokumencie.
 *
 * Dlaczego osobny filtr, a nie któryś z trzech wyżej: `isBrowserExtensionError`
 * i `isInjectedDocumentScriptError` wymagają `frames.length > 0`, a `denyUrls` porównuje adres
 * z ostatniej klatki - **wszystkie trzy sita potrzebują stosu**, którego tu nie ma. To nie jest
 * dziura w tamtych filtrach, tylko ich warunek 1: bez niego wyciszałyby nasze `reportMessage`.
 *
 * Dlaczego nie `ignoreErrors`: wzorzec na `Non-Error promise rejection captured with value` złapałby
 * także odrzucenia niosące payload (`… with keys: error`, Click to Pay) i każde przyszłe odrzucenie
 * obiektem z naszego kodu - czyli dokładnie te, które chcemy widzieć.
 *
 * Pomiar (16.09.2026, grupa 4283 na `sentry.autopay.eu` i #25577698 na starej instancji, od
 * 26.08.2026): 13 zdarzeń u 5 użytkowników przez 3 tygodnie, `browser.name: Safari` i
 * `os.name: Mac OS X` w 13/13, w trzech różnych kontekstach (`payment`, `shoper`, `mojeon`),
 * dokładnie jedno zdarzenie na załadowanie dokumentu, zero klatek i zero breadcrumbów. Żaden
 * użytkownik nie stracił płatności - ścieżki dochodziły do `/confirmation`. Wykluczone jako źródło:
 * nasz kod (każdy `Promise.reject` niesie `response`/`error`/`Error` i zostawiłby breadcrumb
 * `fetch`), `visaMobileLootie.js` (zero `Promise`) oraz SDK Apple Pay z `public/index.html`
 * (jedyny `Promise.reject` w niewywoływanej na tych widokach metodzie, dynamiczne importy z `.catch`).
 *
 * Warunki (wszystkie muszą być spełnione):
 * 1. odrzucenie nie niosło wartości - `hint.originalException` jest `null` albo `undefined`;
 * 2. zdarzenie MA wyjątek - `reportMessage` (`captureMessage`) go nie ma i musi przechodzić dalej;
 * 3. wyjątek pochodzi z `onunhandledrejection` - rzut przez `window.onerror` (`ReferenceError`
 *    ze skryptu wstrzykniętego w dokument) zostaje widoczny;
 * 4. zdarzenie NIE MA ani jednej klatki stosu - odrzucenie ze stosem jest atrybuowalne i przechodzi
 *    przez pozostałe filtry, które potrafią wskazać źródło.
 *
 * ⚠️ Świadomy koszt: gdyby ktoś dopisał w bramce `Promise.reject()` bez `.catch`, to odrzucenie
 * zostanie wyciszone. Sentry i tak nie miałby czym go wskazać - w chwili pisania filtru takiego
 * miejsca w repozytorium nie ma (sprawdzone grepem 16.09.2026).
 *
 * @param {object} event zdarzenie Sentry
 * @param {unknown} originalException surowa wartość odrzucenia (`hint.originalException`)
 * @returns {boolean}
 */
export function isValuelessRejection(event, originalException) {
  if (originalException !== null && originalException !== undefined) {
    return false
  }

  const values = event?.exception?.values
  if (!Array.isArray(values) || values.length === 0) {
    return false
  }

  if (!values.some((value) => value?.mechanism?.type === UNHANDLED_REJECTION_MECHANISM)) {
    return false
  }

  return getEventFrames(event).length === 0
}
